import { Icon } from '@iconify/react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';

const LatestRegisteredOne = () => {
    const { auth } = useAuth();
    const [users, setUsers] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingSubs, setLoadingSubs] = useState(true);
    const [activeTab, setActiveTab] = useState('latestRegistered');

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchLatestUsers = async () => {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/users/latest`, { signal });
                if (response.ok) {
                    const data = await response.json();
                    setUsers(data);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to fetch latest users', error);
                }
            } finally {
                if (!signal.aborted) {
                    setLoadingUsers(false);
                }
            }
        };

        const fetchLatestSubscriptions = async () => {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/latest`, { signal });
                if (response.ok) {
                    const data = await response.json();
                    setSubscriptions(data.subscriptions);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to fetch latest subscriptions', error);
                }
            } finally {
                if (!signal.aborted) {
                    setLoadingSubs(false);
                }
            }
        };

        fetchLatestUsers();
        fetchLatestSubscriptions();

        return () => {
            controller.abort();
        };
    }, [auth]);

    // Helper for badges (copied/adapted from SubscriptionControlLayer)
    const getPlanBadgeClass = (planName) => {
        if (!planName) return 'bg-secondary-focus text-secondary-main';
        const lowerPlan = planName.toLowerCase();
        
        if (lowerPlan.includes('premium')) return 'bg-primary-focus text-primary-main';
        if (lowerPlan.includes('basic')) return 'bg-info-focus text-info-main';
        if (lowerPlan.includes('free')) return 'bg-success-focus text-success-main';
        
        return 'bg-secondary-focus text-secondary-main';
    };

    const getBadgeStyle = (sub) => {
        if (sub.planColor) {
            return {
                backgroundColor: sub.planColor,
                color: '#fff' 
            };
        }
        return {};
    };

    const getBadgeClass = (sub) => {
        if (sub.planColor) {
            return 'badge px-24 py-4 rounded-pill fw-medium text-sm text-nowrap';
        }
        return `badge ${getPlanBadgeClass(sub.plan)} px-24 py-4 rounded-pill fw-medium text-sm text-nowrap`;
    };

    return (
        <div className="col-12">
            <div className="card h-100">
                <div className="card-body p-24">
                    <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                        <ul
                            className="nav border-gradient-tab nav-pills mb-0"
                            id="pills-tab"
                            role="tablist"
                        >
                            <li className="nav-item" role="presentation">
                                <button
                                    className={`nav-link d-flex align-items-center ${activeTab === 'latestRegistered' ? 'active' : ''}`}
                                    id="pills-to-do-list-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-to-do-list"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-to-do-list"
                                    aria-selected={activeTab === 'latestRegistered'}
                                    onClick={() => setActiveTab('latestRegistered')}
                                >
                                    Latest Registered
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    className={`nav-link d-flex align-items-center ${activeTab === 'latestSubscribe' ? 'active' : ''}`}
                                    id="pills-recent-leads-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-recent-leads"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-recent-leads"
                                    aria-selected={activeTab === 'latestSubscribe'}
                                    tabIndex={-1}
                                    onClick={() => setActiveTab('latestSubscribe')}
                                >
                                    Latest Subscribe
                                </button>
                            </li>
                        </ul>
                        <Link
                            to={activeTab === 'latestRegistered' ? '/user-management' : '/subscription-control'}
                            className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
                        >
                            View All
                            <Icon
                                icon="solar:alt-arrow-right-linear"
                                className="icon"
                            />
                        </Link>
                    </div>
                    <div className="tab-content" id="pills-tabContent">
                        <div
                            className={`tab-pane fade ${activeTab === 'latestRegistered' ? 'show active' : ''}`}
                            id="pills-to-do-list"
                            role="tabpanel"
                            aria-labelledby="pills-to-do-list-tab"
                            tabIndex={0}
                        >
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">Users</th>
                                            <th scope="col">Registered On</th>
                                            <th scope="col">Role</th>
                                            <th scope="col" className="text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingUsers ? (
                                            <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                                        ) : users.length === 0 ? (
                                            <tr><td colSpan="4" className="text-center">No users found</td></tr>
                                        ) : (
                                            users.map(user => (
                                                <tr key={user.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="flex-grow-1">
                                                                <h6 className="text-md mb-0 fw-medium">
                                                                    {user.full_name || user.username}
                                                                </h6>
                                                                <span className="text-sm text-secondary-light fw-medium">
                                                                    {user.email}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                                    <td>{user.role}</td>
                                                    <td className="text-center">
                                                        <span className={`px-24 py-4 rounded-pill fw-medium text-sm ${user.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'}`}>
                                                            {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div
                            className={`tab-pane fade ${activeTab === 'latestSubscribe' ? 'show active' : ''}`}
                            id="pills-recent-leads"
                            role="tabpanel"
                            aria-labelledby="pills-recent-leads-tab"
                            tabIndex={0}
                        >
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">Venue Name</th>
                                            <th scope="col">Current Plan</th>
                                            <th scope="col">Subscription Status</th>
                                            <th scope="col">Renewal Date</th>
                                            <th scope="col">Total Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingSubs ? (
                                            <tr><td colSpan="5" className="text-center">Loading...</td></tr>
                                        ) : subscriptions.length === 0 ? (
                                            <tr><td colSpan="5" className="text-center">No subscriptions found</td></tr>
                                        ) : (
                                            subscriptions.map(sub => (
                                                <tr key={sub.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="flex-grow-1">
                                                                <h6 className="text-md mb-0 fw-medium">{sub.venueName}</h6>
                                                                <span className="text-sm text-secondary-light fw-medium">Stripe ID: {sub.stripeId}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span 
                                                            className={getBadgeClass(sub)}
                                                            style={getBadgeStyle(sub)}
                                                        >
                                                            {sub.plan}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${sub.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-24 py-4 rounded-pill fw-medium text-sm`}>
                                                            {sub.status ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1) : 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td>{sub.renewalDate ? new Date(sub.renewalDate).toLocaleDateString() : 'N/A'}</td>
                                                    <td>AED {sub.revenue ? sub.revenue.toFixed(2) : '0.00'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LatestRegisteredOne;
